import {
  ADDER_PROPOSAL_USE_TYPE,
  PrettyUnit,
  UNIT_OF_MEASURE,
} from "@logic/lib/powur-os"

import type {
  BatteryProposalAdderDto,
  BatteryProposalInputsAdderDto,
  BatteryProposalInputsAdderPriceDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalExtendedWarranties from "~/api/hooks/useBatteryProposalExtendedWarranties"
import useBatteryProposalIncentives from "~/api/hooks/useBatteryProposalIncentives"
import useBatteryProposalInputs from "~/api/hooks/useBatteryProposalInputs"
import useBatteryProposalPricing from "~/api/hooks/useBatteryProposalPricing"
import useBatteryProposalVppPrograms from "~/api/hooks/useBatteryProposalVppPrograms"
import useUpdateBatteryProposalAdders from "~/api/hooks/useUpdateBatteryProposalAdders"
import useUpdateBatteryProposalExtendedWarranty from "~/api/hooks/useUpdateBatteryProposalExtendedWarranty"
import useUpdateBatteryProposalIncentives from "~/api/hooks/useUpdateBatteryProposalIncentives"
import useUpdateBatteryProposalVppPrograms from "~/api/hooks/useUpdateBatteryProposalVppPrograms"

import { batterySetupStepPath } from "./setup-steps"
import {
  pricingCommand,
  type AdderCatalog,
  type AdderDraft,
  type AdderQuantityMode,
  type AddersRead,
  type PricingAdder,
  type PricingFinancingLock,
  type PricingKindBundle,
  type PricingPayeeOption,
  type PricingStatusRead,
} from "../../ProposalSetup/pricing/pricing-kind-bundle"
import { VPP_SAVE_FAILED } from "../../ProposalSetup/pricing/PricingVppProgramsStep"
import { FinancingLockNotice } from "../financing-status/concert/FinancingLockNotice"
import { useBatteryFinancingLock } from "../financing-status/concert/use-battery-financing-lock"

export const LOAN_PAYEE_M2_NOTICE = `Applying this incentive to the loan will defer your M2 payment.`

const BATTERY_PAYEE_OPTIONS: readonly PricingPayeeOption[] = [
  { value: `homeowner`, label: `Homeowner`, note: null },
  { value: `powur`, label: `Apply to the loan`, note: LOAN_PAYEE_M2_NOTICE },
]

export const VPP_NO_ELIGIBLE_PROGRAMS = `No VPP programs are eligible for this proposal.`
export const VPP_NO_ELIGIBLE_PROGRAMS_DETAIL = `Eligibility depends on the battery equipment, the property's state and the Finance Plan. Change any of those and check back.`

// --- Adders: the PowurOS Proposal Inputs catalog, read as seller cards -----

const currentPrice = (
  adder: BatteryProposalInputsAdderDto,
): BatteryProposalInputsAdderPriceDto | null => {
  const now = Date.now()
  return (
    (adder.priceList ?? []).find((price) => {
      const start = new Date(price.start).getTime()
      const end = price.end ? new Date(price.end).getTime() : 0
      return start <= now && (end === 0 || end >= now)
    }) ?? null
  )
}

const isRequiredAdder = (adder: BatteryProposalInputsAdderDto): boolean =>
  adder.proposalUseTypeId === ADDER_PROPOSAL_USE_TYPE.REQUIRED

const isSellerVisibleAdder = (adder: BatteryProposalInputsAdderDto): boolean => {
  if (adder.proposalUseTypeId !== ADDER_PROPOSAL_USE_TYPE.MANUAL) {
    return false
  }

  if (adder.title.toLowerCase().startsWith(`multiple arrays`)) {
    return false
  }

  const price = currentPrice(adder)
  if (!price) {
    return false
  }

  return (price.cost === 0 && price.isVariable) || price.cost > 0
}

const isProjectAdder = (adder: BatteryProposalInputsAdderDto): boolean =>
  adder.unit === UNIT_OF_MEASURE.PROJECT

const quantityModeOf = (
  adder: BatteryProposalInputsAdderDto,
  price: BatteryProposalInputsAdderPriceDto | null,
): AdderQuantityMode => {
  if (price?.isVariable) return `price`
  if (isProjectAdder(adder)) return `fixed`
  if (
    adder.unit === UNIT_OF_MEASURE.FOOT ||
    adder.unit === UNIT_OF_MEASURE.SQFT ||
    adder.unit === UNIT_OF_MEASURE.UNIT
  ) {
    return `typed`
  }
  // Battery has no design to derive a quantity from: no input, no text.
  return `derived`
}

const unitLabel = (unit: number | null | undefined): string =>
  PrettyUnit[UNIT_OF_MEASURE[unit ?? 0]] ?? `unit`

const toPricingAdder = (adder: BatteryProposalInputsAdderDto): PricingAdder => {
  const price = currentPrice(adder)
  const required = isRequiredAdder(adder)
  return {
    id: adder.id,
    title: adder.title,
    category: adder.category
      ? {
          id: adder.category.id,
          name: adder.category.name,
          allowMultiples: adder.category.allowMultiples,
        }
      : null,
    required,
    listed: isSellerVisibleAdder(adder),
    quantityMode: quantityModeOf(adder, price),
    unitLabel: unitLabel(adder.unit),
    listPrice: price && !price.isVariable ? price.cost : null,
    derivedQuantity: null,
    defaultQuantity: isProjectAdder(adder) || required ? 1 : null,
  }
}

const savedDraft = (
  catalog: BatteryProposalInputsAdderDto[],
  stored: BatteryProposalAdderDto[],
): Record<number, AdderDraft> => {
  const storedById = new Map(
    stored.map((adder) => [adder.powurOsAdderId, adder]),
  )

  return Object.fromEntries(
    catalog.map((adder) => {
      const saved = storedById.get(adder.id)
      const selected = isRequiredAdder(adder) ? true : (saved?.selected ?? false)
      return [
        adder.id,
        {
          selected,
          customPrice: selected ? (saved?.customPrice ?? null) : null,
          customQuantity: !selected
            ? null
            : isProjectAdder(adder)
              ? 1
              : (saved?.customQuantity ?? null),
        },
      ]
    }),
  )
}

const adderCatalog = (
  catalog: BatteryProposalInputsAdderDto[],
  stored: BatteryProposalAdderDto[],
): AdderCatalog => {
  const listedCategoryIds = new Set(
    catalog
      .filter(isSellerVisibleAdder)
      .map((adder) => adder.category?.id)
      .filter((id): id is number => typeof id === `number`),
  )
  return {
    adders: catalog.map(toPricingAdder),
    categories: [...listedCategoryIds].map((id) => ({
      id,
      name:
        catalog.find((adder) => adder.category?.id === id)?.category?.name ??
        `Category`,
    })),
    saved: savedDraft(catalog, stored),
  }
}

const useBatteryAdders = (proposalId: string): AddersRead => {
  const proposalQuery = useBatteryProposal(proposalId)
  const inputsQuery = useBatteryProposalInputs(proposalId)
  return {
    loading: inputsQuery.isLoading,
    loadError: inputsQuery.isError
      ? `Unable to load available adders`
      : proposalQuery.isError
        ? `Unable to load saved adders`
        : null,
    retry: () => {
      if (inputsQuery.isError) {
        void inputsQuery.refetch()
      }
      if (proposalQuery.isError) {
        void proposalQuery.refetch()
      }
    },
    catalog: inputsQuery.isSuccess
      ? adderCatalog(inputsQuery.data.adders, proposalQuery.data?.adders ?? [])
      : null,
    ready: inputsQuery.isSuccess && proposalQuery.isSuccess,
  }
}

// --- Kind-level reads -----------------------------------------------------

const useBatteryPricingFinancingLock = (
  proposalId: string,
): PricingFinancingLock => {
  const lock = useBatteryFinancingLock(proposalId)
  return {
    locked: lock.locked,
    pending: lock.pending,
    notice: <FinancingLockNotice proposalId={proposalId} lock={lock} />,
  }
}

const useBatteryPricingStatus = (proposalId: string): PricingStatusRead => {
  const query = useBatteryProposalPricing(proposalId)
  return {
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    hasPricing: query.data?.pricing != null,
    refetch: query.refetch,
  }
}

/** Battery-Only Proposal reads and commands for the shared pricing steps. */
export const batteryPricingBundle: PricingKindBundle = {
  systemPricingPath: (proposalId) =>
    batterySetupStepPath(proposalId, `system-pricing`),
  useFinancingLock: useBatteryPricingFinancingLock,
  usePricingStatus: useBatteryPricingStatus,
  adders: {
    copy: { saveLabel: `Save adders` },
    useAdders: useBatteryAdders,
    useSave: (proposalId) =>
      pricingCommand(useUpdateBatteryProposalAdders(), (body) => ({
        proposalId,
        body: { adders: [...body.adders] },
      })),
    describeSaveError: () => `Unable to save adders`,
    useCompanion: () => null,
  },
  warranty: {
    copy: {
      title: `Extended Protection`,
      loading: `Loading Extended Protection`,
      empty: `No Extended Protection available`,
      loadFailed: `Unable to load Extended Protection`,
    },
    useCatalog: useBatteryProposalExtendedWarranties,
    // The saved snapshot is proposal-owned, so it renders from the main
    // proposal read.
    useSavedWhenCatalogFails: (proposalId) =>
      useBatteryProposal(proposalId).data?.extendedWarranty ?? null,
    useSave: (proposalId) =>
      pricingCommand(
        useUpdateBatteryProposalExtendedWarranty(),
        (warrantyId: number | null) => ({ proposalId, body: { warrantyId } }),
      ),
  },
  incentives: {
    copy: {
      empty: `No eligible battery incentives`,
      pricingRequiredDetail: `Incentive values are calculated from the stored Battery System Pricing. Calculate pricing first, then come back to pick incentives.`,
    },
    usePayeeOptions: () => BATTERY_PAYEE_OPTIONS,
    useIncentives: useBatteryProposalIncentives,
    useSave: (proposalId) =>
      pricingCommand(useUpdateBatteryProposalIncentives(), (body) => ({
        proposalId,
        body,
      })),
    describeSaveError: () =>
      `Unable to save incentives. Your changes are still here.`,
    isPricingRequiredError: () => false,
    Toolbar: null,
  },
  vpp: {
    copy: {
      empty: VPP_NO_ELIGIBLE_PROGRAMS,
      emptyDetail: VPP_NO_ELIGIBLE_PROGRAMS_DETAIL,
    },
    // Battery acknowledges removals on save without naming them.
    showRemovedPrograms: false,
    usePrograms: useBatteryProposalVppPrograms,
    useSave: (proposalId) =>
      pricingCommand(useUpdateBatteryProposalVppPrograms(), (body) => ({
        proposalId,
        body,
      })),
    describeSaveError: () => VPP_SAVE_FAILED,
  },
}
